import { useCallback, useMemo, useRef } from "react";
import type { PageContentRow } from "../pages/types";

const MAX_HISTORY = 200;

export interface UndoRedoHandle {
  /** Record a state snapshot (the new current state) in history. */
  push: (snapshot: PageContentRow[]) => void;
  /** Move back one step. Returns the restored snapshot or null. */
  undo: () => PageContentRow[] | null;
  /** Move forward one step. Returns the restored snapshot or null. */
  redo: () => PageContentRow[] | null;
  canUndo: () => boolean;
  canRedo: () => boolean;
  clear: () => void;
}

/**
 * Session-scoped undo/redo stack using structuredClone.
 * Does NOT clear on draft save (per D15). Clears on editor unmount.
 *
 * Model: history stores complete state snapshots. The pointer indicates
 * which snapshot is the current state. push() records the new state after
 * a mutation. undo/redo move the pointer and return the target snapshot.
 */
export function useUndoRedo(): UndoRedoHandle {
  const historyRef = useRef<PageContentRow[][]>([]);
  const pointerRef = useRef(-1);

  const push = useCallback((snapshot: PageContentRow[]) => {
    const clone = structuredClone(snapshot);
    // Trim anything ahead of pointer (discard redo-forward branch)
    historyRef.current = historyRef.current.slice(0, pointerRef.current + 1);
    historyRef.current.push(clone);
    // Cap at MAX_HISTORY
    if (historyRef.current.length > MAX_HISTORY) {
      historyRef.current = historyRef.current.slice(
        historyRef.current.length - MAX_HISTORY,
      );
    }
    pointerRef.current = historyRef.current.length - 1;
  }, []);

  const undo = useCallback((): PageContentRow[] | null => {
    if (pointerRef.current <= 0) return null;
    pointerRef.current--;
    return structuredClone(historyRef.current[pointerRef.current]);
  }, []);

  const redo = useCallback((): PageContentRow[] | null => {
    if (pointerRef.current >= historyRef.current.length - 1) return null;
    pointerRef.current++;
    return structuredClone(historyRef.current[pointerRef.current]);
  }, []);

  const canUndo = useCallback(() => pointerRef.current > 0, []);
  const canRedo = useCallback(
    () => pointerRef.current < historyRef.current.length - 1,
    [],
  );

  const clear = useCallback(() => {
    historyRef.current = [];
    pointerRef.current = -1;
  }, []);

  // Stable reference -- prevents dependent useEffects from re-firing every
  // render (all individual callbacks have empty deps, so this never changes).
  return useMemo(
    () => ({ push, undo, redo, canUndo, canRedo, clear }),
    [push, undo, redo, canUndo, canRedo, clear],
  );
}
